import {
  DEMAND_FEE_TIERS,
  INTEGRATION_PRICING,
  formatDemandFeePrice,
  formatIntegrationPrice,
} from './settingsStore.js';
import { pickVariant, withJetOpener } from './jetVoice.js';
import { isJetSmalltalk, replyToJetSmalltalk } from './jetSmalltalk.js';

const TIER_ONE = DEMAND_FEE_TIERS.find((tier) => tier.id === 'tier1') || DEMAND_FEE_TIERS[0];
const DEMAND_PRICE = formatDemandFeePrice(TIER_ONE.perDemandPrice);
const CONNECTION_PRICE = formatIntegrationPrice(INTEGRATION_PRICING.connectionMonthly);

function textOf(message) {
  return String(message || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function reply(text, links = []) {
  return { text, links };
}

function say(options, links = [], context = {}) {
  const picked = pickVariant(options, context.seed);
  const text = context.skipOpener ? picked : withJetOpener(picked, { seed: context.seed });
  return reply(text, links);
}

/**
 * Local FAQ intents that should stay on canned replies (accurate prices/settings).
 */
export function isJetLocalFaqIntent(message, role = 'CLIENT', context = {}) {
  const q = textOf(message);
  const who = String(role || 'CLIENT').toUpperCase();
  const onIntake = Boolean(context?.onIntake);
  if (!q) return true;
  if (isJetSmalltalk(message)) return true;
  if (/^(hi|hello|hey|yo|howdy|good morning|good afternoon)\b/.test(q) || q === 'jet') {
    return true;
  }
  if (/who are you|what are you|your name|about you/.test(q)) return true;
  if (/paid before|before assign|no attorney/.test(q)) return true;
  if (/price|pricing|cost|fee|charge|how much|billing/.test(q)) return true;
  if (/quickbooks|qbo|\bxero\b|freshbooks|integrat|ledger|accounting/.test(q)) return true;
  if (who === 'ADMIN' && /route|assign|attorney/.test(q)) return true;
  if (who === 'ATTORNEY' && /track|certified|usps|mail|postage/.test(q)) return true;
  if (who === 'ATTORNEY' && /draft|letter|queue|jurisdiction|accept/.test(q)) return true;
  if (
    onIntake &&
    /fill|extract|invoice|upload|evidence|read (the )?file|do (it|this) for me|auto.?fill/.test(q) &&
    !/\?/.test(String(message || ''))
  ) {
    return true;
  }
  if (/evidence|document|upload|attachment|locker|vault|contract/.test(q)) return true;
  if (/report|collection|pipeline|dashboard|kpi/.test(q)) return true;
  if (/debtor|customer|account record|who owes/.test(q)) return true;
  if (/message|chat with|reply to (the )?attorney|inbox/.test(q)) return true;
  if (/learn|terms|policy|archive storage|how demandjet works/.test(q)) return true;
  if (/^(send a demand|find a debtor|what does it cost|connect quickbooks)\b/.test(q)) return true;
  if (/demand|send|intake|new case|kanban|matter/.test(q) && !/\?/.test(String(message || ''))) {
    return true;
  }
  return false;
}

/**
 * Short product answers for Jet. Prices come from the same billing constants as Guides.
 * @param {string} message
 * @param {string} [role]
 * @param {{ onIntake?: boolean, seed?: number, skipOpener?: boolean }} [context]
 */
export function replyToJet(message, role = 'CLIENT', context = {}) {
  const q = textOf(message);
  const who = String(role || 'CLIENT').toUpperCase();
  const onIntake = Boolean(context?.onIntake);
  const seed = context.seed;

  if (!q) {
    return onIntake
      ? say(
          [
            'Drop an invoice here, or ask me anything about this form.',
            'Upload evidence and I’ll fill what I can — or just ask a question.',
          ],
          [],
          { ...context, skipOpener: true, seed }
        )
      : say(
          [
            'Ask me about sending a demand, finding a debtor, or pricing.',
            'What do you want to do — new demand, debtor lookup, or pricing?',
          ],
          [],
          { ...context, skipOpener: true, seed }
        );
  }

  if (isJetSmalltalk(message)) {
    return replyToJetSmalltalk(message, { seed, role: who });
  }

  if (/^(hi|hello|hey|yo|howdy|good morning|good afternoon)\b/.test(q) || q === 'jet') {
    return jetGreeting(who, { ...context, onIntake, seed });
  }

  if (
    onIntake &&
    /fill|extract|invoice|upload|evidence|read (the )?file|do (it|this) for me|auto.?fill/.test(q) &&
    !/\?/.test(String(message || ''))
  ) {
    return say(
      [
        'Attach an invoice here and I’ll pull the fields for you to review before applying.',
        'Drop the file in this chat — I’ll read it, then you confirm before it hits the form.',
        'Sure. Upload evidence here and we’ll fill this together.',
      ],
      [],
      { seed, skipOpener: true }
    );
  }

  if (/who are you|what are you|your name|about you/.test(q)) {
    return say(
      [
        "I’m Jet. I help you get demands out the door — forms, evidence, pricing, and the awkward jurisdiction questions.",
        "Jet here. Think of me as the teammate who knows where the buttons are and when to ping an attorney.",
      ],
      [],
      { seed, skipOpener: true }
    );
  }

  if (/paid before|before assign|no attorney/.test(q)) {
    return say(
      [
        'If they pay in full before an attorney accepts, it closes as paid-before-assignment — no attorney route, no per-demand fee.',
        'Full payoff while it’s still unassigned? You’re done, no attorney fee. Once an attorney has accepted, a payment is a normal paid matter and the fee still applies.',
      ],
      who === 'CLIENT' ? [{ label: 'Read the terms', to: '/client/guides' }] : [],
      { seed }
    );
  }

  if (/price|pricing|cost|fee|charge|how much|billing/.test(q)) {
    return say(
      [
        `Published tiers start at ${DEMAND_PRICE} per demand (Tier 1) — only when an attorney accepts. Trailing volume can lower the rate; drafting is free, and we take 0% of recoveries. Ledger connections are ${CONNECTION_PRICE}/mo.`,
        `Pricing is simple: ${DEMAND_PRICE}+ when an attorney accepts, based on your volume tier. No charge to draft or send. Connecting QuickBooks/Xero/FreshBooks is ${CONNECTION_PRICE}/mo.`,
      ],
      who === 'ATTORNEY'
        ? []
        : [
            { label: 'Guides · terms', to: '/client/guides' },
            { label: 'Settings', to: '/client/settings' },
          ],
      { seed }
    );
  }

  if (/quickbooks|qbo|\bxero\b|freshbooks|integrat|ledger|accounting/.test(q)) {
    return say(
      [
        `Hook up QuickBooks, Xero, or FreshBooks in Settings (${CONNECTION_PRICE}/mo each). Then you can push open invoices out as demands.`,
        `Settings → connect your ledger (${CONNECTION_PRICE}/mo). After that, sending invoice-backed demands is a lot less typing.`,
      ],
      who === 'CLIENT'
        ? [
            { label: 'Open Settings', to: '/client/settings' },
            { label: 'Go to Demands', to: '/client/demands' },
          ]
        : [{ label: 'Open Settings', to: '/settings' }],
      { seed }
    );
  }

  if (who === 'ADMIN' && /route|assign|attorney/.test(q)) {
    return say(
      [
        'In Routing engine, enter the matter id and run autoRouteAttorney — that parks a licensed attorney on it.',
        'Use autoRouteAttorney with the matter id from Routing engine. Clients still work the same demand from their workspace.',
      ],
      [],
      { seed }
    );
  }

  if (who === 'ATTORNEY' && /track|certified|usps|mail|postage/.test(q)) {
    return say(
      [
        'Open the matter, drop in the certified-mail tracking number, and save — I’ll surface the carrier for the client.',
        'Add the tracking number on the matter and hit save. The client can follow delivery from there.',
      ],
      [],
      { seed }
    );
  }

  if (who === 'ATTORNEY' && /draft|letter|queue|jurisdiction|accept/.test(q)) {
    return say(
      [
        'Your jurisdiction queue is the inbox. Grab a matter, tweak the draft, save it, and message the client from that record.',
        'Work from the queue: open a matter, edit the demand draft, save, then reply on the matter thread.',
      ],
      [],
      { seed }
    );
  }

  if (/evidence|document|upload|attachment|locker|vault|contract/.test(q)) {
    if (onIntake) {
      return say(
        [
          'Attach files right here and I’ll fill what I can.',
          'Drop invoices or proof in this chat — I’ll read them before anything hits the form.',
        ],
        [],
        { seed, skipOpener: true }
      );
    }
    return say(
      [
        'Park contracts and invoices in Evidence. You can also attach files while drafting a demand or messaging.',
        'Evidence locker is home base for proof. Attach on the demand or in messages when you’re in the flow.',
      ],
      who === 'CLIENT'
        ? [
            { label: 'Open Evidence', to: '/client/evidence-locker' },
            { label: 'New demand', to: '/client/demands/new' },
          ]
        : [],
      { seed }
    );
  }

  if (/report|collection|pipeline|dashboard|kpi/.test(q)) {
    return say(
      [
        'Reports splits pipeline stages from collections — what’s still open vs what’s come back in.',
        'Need numbers? Pipeline shows stage counts; Collections shows recovered vs outstanding.',
      ],
      who === 'CLIENT'
        ? [
            { label: 'Pipeline', to: '/client/reports' },
            { label: 'Collections', to: '/client/reports/collections' },
          ]
        : [],
      { seed }
    );
  }

  if (/debtor|customer|account record|who owes/.test(q)) {
    return say(
      [
        'Debtors are the people/companies you collect from. Create one first, or open an existing record to see every demand on them.',
        'Start from Debtors if you want the full history. New debtor is there when it’s a fresh account.',
      ],
      who === 'CLIENT'
        ? [
            { label: 'Debtors', to: '/client/debtors' },
            { label: 'New debtor', to: '/client/debtors/new' },
          ]
        : [],
      { seed }
    );
  }

  if (/message|chat with|reply to (the )?attorney|inbox/.test(q)) {
    return say(
      [
        'Messages is the attorney thread. Open it from Messages, or write from the demand itself.',
        'Talk to the assigned attorney on the matter thread — Messages lists every conversation.',
      ],
      who === 'CLIENT' ? [{ label: 'Messages', to: '/client/messages' }] : [],
      { seed }
    );
  }

  if (/learn|guides|terms|policy|archive storage|how demandjet works/.test(q)) {
    return say(
      [
        'Guides has the straight talk on fees, when you’re charged, archive storage, and add-ons.',
        'If you want the official wording, Guides stays synced with billing and terms.',
      ],
      who === 'CLIENT' ? [{ label: 'Open Guides', to: '/client/guides' }] : [],
      { seed }
    );
  }

  if (/demand|send|intake|new case|kanban|matter|collect/.test(q)) {
    if (who === 'ATTORNEY') {
      return say(
        [
          'Ready matters show up in your jurisdiction queue. Open one, update the draft, and message the client from there.',
          'Queue → matter → draft → save. Message the client on that same record when you need clarification.',
        ],
        [],
        { seed }
      );
    }
    if (who === 'ADMIN') {
      return say(
        [
          'Clients work demands in their workspace; you route attorneys from Internal Ops with autoRouteAttorney.',
          'When something needs an attorney, run autoRouteAttorney with the matter id from routing.',
        ],
        [],
        { seed }
      );
    }
    return say(
      [
        'Hit New demand, add the debtor and amount, attach evidence if you’ve got it. You only pay when an attorney accepts — not when you draft.',
        'Start a New demand, fill the basics, and watch it on the kanban. The fee waits until an attorney accepts.',
      ],
      [
        { label: 'New demand', to: '/client/demands/new' },
        { label: 'Kanban', to: '/client/demands/kanban' },
      ],
      { seed }
    );
  }

  if (who === 'ATTORNEY') {
    return say(
      [
        'I can help with the queue, drafts, mail tracking, or pricing — what do you need?',
        'Queue, draft edits, tracking, or fees — point me at one and I’ll walk you through it.',
      ],
      [],
      { seed, skipOpener: true }
    );
  }
  if (who === 'ADMIN') {
    return say(
      [
        'I can cover attorney routing, demand fees, or where clients send work. What should we tackle?',
        'Routing, pricing, or the client workspace — which one?',
      ],
      [],
      { seed, skipOpener: true }
    );
  }
  return say(
    [
      'I can help you send a demand, find a debtor, stash evidence, or check pricing. What do you want to do?',
      'New demand, debtor lookup, evidence, or pricing — your call.',
    ],
    [
      { label: 'New demand', to: '/client/demands/new' },
      { label: 'Debtors', to: '/client/debtors' },
      { label: 'Guides', to: '/client/guides' },
    ],
    { seed, skipOpener: true }
  );
}

export function jetGreeting(role = 'CLIENT', context = {}) {
  const who = String(role || 'CLIENT').toUpperCase();
  const seed = context.seed;
  if (who === 'ATTORNEY') {
    return say(
      [
        'Hey — I’m Jet. Ask about the jurisdiction queue, drafts, or mail tracking.',
        'Jet here. Queue, drafts, tracking — I’ve got you.',
      ],
      [],
      { seed, skipOpener: true }
    );
  }
  if (who === 'ADMIN') {
    return say(
      [
        'Hey — I’m Jet. Ask me about routing an attorney or how fees work.',
        'Jet here. Routing and fees are my usual admin questions.',
      ],
      [],
      { seed, skipOpener: true }
    );
  }
  if (context?.onIntake) {
    return say(
      [
        'You’re on Create Demand. Upload an invoice in this chat and I’ll fill the form — you review before it applies.',
        'Create Demand is open. Drop evidence here and I’ll do the heavy lifting on the fields.',
      ],
      [],
      { seed, skipOpener: true }
    );
  }
  return say(
    [
      'Hey — I’m Jet. I can help you send a demand, find a debtor, or check pricing.',
      'Jet here. Need a demand started, a debtor found, or a quick pricing check?',
    ],
    [
      { label: 'New demand', to: '/client/demands/new' },
      { label: 'Debtors', to: '/client/debtors' },
    ],
    { seed, skipOpener: true }
  );
}

export function jetSuggestions(role = 'CLIENT', context = {}) {
  const who = String(role || 'CLIENT').toUpperCase();
  if (who === 'ATTORNEY') {
    return ['How does the queue work?', 'Add mail tracking', 'What does a demand cost?'];
  }
  if (who === 'ADMIN') {
    return ['Route an attorney', 'What does a demand cost?', 'Where do clients send demands?'];
  }
  if (context?.onIntake) {
    return ['Fill this form from an invoice', 'What does a demand cost?', 'Will you accept a payment plan?'];
  }
  return [
    'Send a demand',
    'Find a debtor',
    'What does it cost?',
    'Can I add collection costs in Ohio?',
  ];
}

/** Short launcher bubble copy when the client is on Create Demand. */
export function jetIntakeNudgeText() {
  return "Upload your evidence in my chat - I'll fill this form for you";
}
